import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { cn } from '@/lib/utils';

interface CompactTask {
  id: string;
  title: string;
  priority?: string;
  dueDate?: string;
  assignee?: { id: string; name: string; avatarUrl?: string };
  assigneeId?: string;
  statusId?: string;
  statusConfig?: { id: string; name: string; color: string; isClosed: boolean };
}

interface StatusConfig {
  id: string;
  name: string;
  color: string;
  position?: number;
  isClosed: boolean;
}

type Hover = { task: CompactTask; status: StatusConfig; rect: DOMRect };

/** Max pills rendered per status column before collapsing into a "See all" link. */
const MAX_PER_COLUMN = 6;

/** Droppable id for a status column, namespaced by list so pills can cross lists. */
const columnId = (listId: string, statusId: string) => `${listId}::${statusId}`;

const fmtDate = (d?: string) => {
  if (!d) return null;
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? null : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const statusOf = (t: CompactTask) => t.statusId || t.statusConfig?.id;

function Pill({
  task,
  status,
  listId,
  draggable,
  onClick,
  onHover,
}: {
  task: CompactTask;
  status: StatusConfig;
  listId: string;
  draggable: boolean;
  onClick: () => void;
  onHover: (h: Hover | null, taskId?: string) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: task.id,
    disabled: !draggable,
    data: { type: 'compact-pill', listId, statusId: status.id, color: status.color },
  });
  return (
    <button
      ref={setNodeRef}
      type="button"
      {...attributes}
      {...listeners}
      onClick={onClick}
      onMouseEnter={(e) => onHover({ task, status, rect: e.currentTarget.getBoundingClientRect() })}
      onMouseLeave={() => onHover(null, task.id)}
      aria-label={task.title}
      className={cn(
        'block w-full rounded-md border px-2 py-1 text-left transition-colors hover:brightness-95',
        draggable && 'cursor-grab active:cursor-grabbing',
        isDragging && 'opacity-40',
      )}
      style={{ backgroundColor: `${status.color}1f`, borderColor: `${status.color}55` }}
    >
      <span className="block truncate text-xs font-medium text-secondary">{task.title}</span>
    </button>
  );
}

function Column({
  status,
  listId,
  items,
  draggable,
  onTaskClick,
  onHover,
  onSeeAll,
}: {
  status: StatusConfig;
  listId: string;
  items: CompactTask[];
  draggable: boolean;
  onTaskClick?: (task: CompactTask) => void;
  onHover: (h: Hover | null, taskId?: string) => void;
  onSeeAll?: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: columnId(listId, status.id),
    data: { type: 'compact-column', listId, statusId: status.id },
  });
  const visible = items.slice(0, MAX_PER_COLUMN);
  const overflow = items.length - visible.length;
  return (
    <div
      ref={setNodeRef}
      className={cn('flex-1 min-w-[8rem] rounded-lg p-1 transition-colors', isOver && 'bg-accent/5 ring-2 ring-inset ring-accent/50')}
    >
      {/* Column header */}
      <div
        className="mb-2 flex items-center gap-1.5 rounded-md bg-grey-50 px-2 py-1"
        style={{ borderLeft: `3px solid ${status.color}` }}
      >
        <span className="truncate text-xs font-semibold text-secondary">{status.name}</span>
        <span className="ml-auto rounded-full bg-grey-200 px-1.5 text-[11px] font-medium text-grey-600">
          {items.length}
        </span>
      </div>

      {/* Task pills */}
      <div className="space-y-1">
        {items.length === 0 && <div className="px-1 py-1 text-[11px] text-grey-300">No tasks</div>}
        {visible.map((t) => (
          <Pill
            key={t.id}
            task={t}
            status={status}
            listId={listId}
            draggable={draggable}
            onClick={() => onTaskClick?.(t)}
            onHover={onHover}
          />
        ))}
        {overflow > 0 && (
          <button
            type="button"
            onClick={onSeeAll}
            className="block w-full rounded-md px-2 py-1 text-left text-[11px] font-medium text-accent hover:bg-grey-50"
          >
            See all {items.length} →
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Compact horizontal kanban: one column per status, laid out left→right, with a
 * small pill per task. When `draggable`, pills register as draggables in the
 * ambient DndContext (owned by ProjectTaskLists) so they can be dropped on any
 * status column — including columns belonging to other lists. Hover a pill for
 * details; click to open.
 */
export function CompactBoard({
  listId,
  tasks,
  statusConfigs,
  onTaskClick,
  onSeeAll,
  draggable = false,
}: {
  listId: string;
  tasks: CompactTask[];
  statusConfigs: StatusConfig[];
  onTaskClick?: (task: CompactTask) => void;
  /** Navigate to the list detail view; shown as "See all" when a column overflows. */
  onSeeAll?: () => void;
  /** When true, pills are draggable (cross-list status changes via the ambient context). */
  draggable?: boolean;
}) {
  const [hover, setHover] = useState<Hover | null>(null);

  const byStatus = useMemo(() => {
    const map = new Map<string, CompactTask[]>();
    for (const s of statusConfigs) map.set(s.id, []);
    for (const t of tasks) {
      const sid = statusOf(t);
      if (sid && map.has(sid)) map.get(sid)!.push(t);
    }
    return map;
  }, [tasks, statusConfigs]);

  const ordered = useMemo(
    () => [...statusConfigs].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
    [statusConfigs],
  );

  const onHover = (h: Hover | null, taskId?: string) => {
    // Ignore a stale mouse-leave from a pill we've already left.
    setHover((prev) => (h === null && taskId && prev?.task.id !== taskId ? prev : h));
  };

  const due = hover ? fmtDate(hover.task.dueDate) : null;

  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {ordered.map((s) => (
        <Column
          key={s.id}
          status={s}
          listId={listId}
          items={byStatus.get(s.id) ?? []}
          draggable={draggable}
          onTaskClick={onTaskClick}
          onHover={onHover}
          onSeeAll={onSeeAll}
        />
      ))}

      {/* Hover card — portaled to <body> so overflow never clips it */}
      {hover &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[60] max-w-[18rem] rounded-lg border border-grey-200 bg-white px-3 py-2 shadow-elevated"
            style={{
              left: Math.max(8, Math.min(hover.rect.left, window.innerWidth - 296)),
              top: hover.rect.top - 8,
              transform: 'translateY(-100%)',
            }}
          >
            <span className="block truncate text-sm font-medium text-secondary">{hover.task.title}</span>
            <span className="mt-0.5 flex items-center gap-2 text-xs text-grey-500">
              <span className="inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: hover.status.color }} />
                {hover.status.name}
              </span>
              {hover.task.assignee?.name && <span className="truncate">· {hover.task.assignee.name}</span>}
              {hover.task.priority && <span className="capitalize">· {hover.task.priority.toLowerCase()}</span>}
              {due && <span>· {due}</span>}
            </span>
          </div>,
          document.body,
        )}
    </div>
  );
}
